/**
 * Contact form – Form 클래스 활용, 캡차 새로고침
 * id="contact-form", data-submit-url, data-captcha-url
 * Submit: .contact-form-btn-label + .contact-form-btn-loader
 */

import { Form } from "../../core/Form";
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";

const FORM_ID = "contact-form";
const BTN_LOADER_CLASS = "contact-form-btn-loader";

function getForm(): HTMLFormElement | null {
  return document.getElementById(FORM_ID) as HTMLFormElement | null;
}

function getCaptchaImg(form: HTMLFormElement): HTMLImageElement | null {
  const id = "contact-captcha-img";
  const el = form.querySelector<HTMLImageElement>(`#${id}`);
  return el ?? (document.getElementById(id) as HTMLImageElement | null);
}

function refreshCaptchaImage(form: HTMLFormElement): void {
  const url = form.dataset.captchaUrl;
  if (!url) return;
  const img = getCaptchaImg(form);
  if (img) img.src = `${url}?v=${Date.now()}`;
}

function getMessages(): Record<string, string> {
  const lang = document.documentElement.lang || "ko";
  const isKo = lang.startsWith("ko");
  return {
    nameRequired: isKo ? "이름을 입력해주세요." : "Please enter your name.",
    emailRequired: isKo ? "이메일을 입력해주세요." : "Please enter your email.",
    subjectRequired: isKo ? "제목을 입력해주세요." : "Please enter a subject.",
    messageRequired: isKo ? "내용을 입력해주세요." : "Please enter your message.",
    captchaRequired: isKo ? "보안코드를 입력해주세요." : "Please enter the security code.",
    requestFailed: isKo ? "전송에 실패했습니다." : "Request failed.",
    errorOccurred: isKo ? "오류가 발생했습니다." : "An error occurred.",
  };
}

function validateContact(form: HTMLFormElement): string | null {
  const messages = getMessages();
  const name = (form.querySelector<HTMLInputElement>('[name="name"]')?.value ?? "").trim();
  const email = (form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? "").trim();
  const subject = (form.querySelector<HTMLInputElement>('[name="subject"]')?.value ?? "").trim();
  const contents = (form.querySelector<HTMLTextAreaElement>('[name="contents"]')?.value ?? "").trim();
  const rCaptcha = (form.querySelector<HTMLInputElement>('[name="r_captcha"]')?.value ?? "").trim();

  if (!name) return messages.nameRequired;
  if (!email) return messages.emailRequired;
  if (!subject) return messages.subjectRequired;
  if (!contents) return messages.messageRequired;
  if (!rCaptcha) return messages.captchaRequired;
  return null;
}

export class ContactFormController {
  static form(): void {
    Logger.info("ContactFormController.form");
    const form = getForm();
    if (!form) return;

    const submitUrl = form.dataset.submitUrl;
    if (!submitUrl) return;

    const messages = getMessages();

    const f = new Form(form, {
      submitUrl,
      validate: validateContact,
      loaderSelector: `.${BTN_LOADER_CLASS}`,
      requestFailedMessage: messages.requestFailed,
      invalidResponseMessage: messages.errorOccurred,
      onSuccess: (data) => {
        const msg = (data?.msg ?? data?.message ?? messages.errorOccurred) as string;
        void Modal.alert(msg);
        form.reset();
        refreshCaptchaImage(form);
      },
      onFail: (message: string) => {
        void Modal.alert(message || messages.errorOccurred);
        refreshCaptchaImage(form);
      },
    });
    f.init();

    const img = getCaptchaImg(form);
    if (img) {
      img.addEventListener("click", () => refreshCaptchaImage(form));
      img.style.cursor = "pointer";
    }
  }
}

/** @deprecated Use ContactFormController.form() via controller dispatch */
export function initContactForm(): void {
  ContactFormController.form();
}
